import React, { useContext, useEffect, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DataHandlerContext } from "../../providers/DataHandlerProvider";
import { useParams } from "react-router-dom";
import { DataContext } from "../../providers/DataProvider";
import TopTitle from "../buttons/TopTitle";
import DraggableField from "./DraggableField";
import DeleteModal from "../modal/DeleteModal";
import BackButton from "../buttons/BackButton";

function CreateDynamicForm() {
  const [formFields, setFormFields] = useState([]);
  const [addFormFields, setAddFormFields] = useState([]);
  const [selectedField, setSelectedField] = useState("input");
  const [label, setLabel] = useState("");
  const [required, setRequired] = useState(false);
  const [showOnSlack, setShowOnSlack] = useState(false);
  
  const [reOrderFlag, setReOrderFlag] = useState(false);
  const [draggedIds, setDraggedIds] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFieldId, setDeleteFieldId] = useState(null);
  const [showFieldLabelEmptyError, setShowFieldLabelEmptyError] =
    useState(false);
  const [showSelectFieldError, setShowSelectFieldError] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [trackSelectFieldInputFields, setTrackSelectFieldInputFields] =
    useState([]);
  const [inputFields, setInputFields] = useState([""]);

  const {
    addMarkFormFields,
    updateEventSeriesMarkFormField,
    updateMarkFormField,
    handleDeleteMarkFormField,
  } = useContext(DataHandlerContext);
  const { populateEachMarkForm, handlePopulateEachMarkForm } =
    useContext(DataContext);

  const { formid } = useParams();

  useEffect(() => {
    if (formid) {
      handlePopulateEachMarkForm(formid);
    }
  }, [formid, handlePopulateEachMarkForm]);

  useEffect(() => {
    console.log("PopulateEachMarkForm updated:", populateEachMarkForm);
    if (populateEachMarkForm && populateEachMarkForm.formFields) {
      console.log("Setting form fields:", populateEachMarkForm.formFields);
      setFormFields(populateEachMarkForm.formFields);
    }
  }, [populateEachMarkForm]);

  const handleDeleteModal = (value, id) => {
    console.log("Delete Modal:", id);
    setShowDeleteModal(value);
    setDeleteFieldId(id);
  };

  const handleSelectField = (event) => {
    setSelectedField(event.target.value);
  };

  const handleDeleteFormField = async (confirm, fieldId) => {
    if (confirm) {
      console.log("Deleting field:", fieldId);

      const updatedFields = formFields.filter((field) => field.id !== fieldId);
      const updatedAddFields = addFormFields.filter(
        (field) => field.id !== fieldId
      );

      setFormFields(updatedFields);
      setAddFormFields(updatedAddFields);

      try {
        const response = await handleDeleteMarkFormField(formid, fieldId);
        console.log("Response:", response);

        handlePopulateEachMarkForm(formid);
      } catch (error) {
        console.error("Error deleting field:", error);
        alert("Error deleting field. Please try again.");
      }
    }
    setShowDeleteModal(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!active || !over) {
      console.log("Missing active or over in drag end event");
      return;
    }

    console.log("Drag end:", { active: active.id, over: over.id });
    setDraggedIds((prevIds) => {
      const newIds = new Set(prevIds);
      newIds.add(active.id);
      newIds.add(over.id);
      return newIds;
    });
    setReOrderFlag(true);

    if (active.id !== over.id) {
      setFormFields((fields) => {
        const oldIndex = fields.findIndex((field) => field.id === active.id);
        const newIndex = fields.findIndex((field) => field.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          console.log("Could not find indexes:", { oldIndex, newIndex });
          return fields;
        }

        console.log("Moving from index", oldIndex, "to", newIndex);
        const newFields = arrayMove(fields, oldIndex, newIndex);

        const updatedFields = newFields.map((field, index) => ({
          ...field,
          order: index,
        }));

        return updatedFields;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    //Check if the field label is empty
    if (label.trim() === "") {
      setShowFieldLabelEmptyError(true);
      setErrorMessage("This field cannot be empty");
      return;
    }

    //Check if the select field has empty input fields
    if (selectedField === "select") {

      const hasEmptyInputField = inputFields.some((input) => input.trim() === "");
      console.log("Input fields:", inputFields);
      if (hasEmptyInputField) {
        setShowSelectFieldError(true);
        
        return;
      }
    }



    const newField = {
      type: selectedField,
      label,
      required,
      order: formFields.length + 1,
      options: selectedField === "select" ? inputFields : [],
      show_on_slack: showOnSlack,
    };

    setLabel("");
    setShowOnSlack(false);
    setInputFields([""]);
    setRequired(false);

    try {
      const response = await addMarkFormFields([newField], formid);
      console.log("Response:", response);

      if (response && response.data && response.data.length > 0) {
        const savedField = response.data[0];

        setFormFields((prevFields) => [...prevFields, savedField]);
        setAddFormFields((prevFields) => [...prevFields, savedField]);
      } else {
        handlePopulateEachMarkForm(formid);
      }
    } catch (error) {
      console.error("Error adding field:", error);
      alert("Error adding field. Please try again.");
    }
  };

  const handleUpdateFieldOrder = async (event) => {
    event.preventDefault();
    console.log("Submitting form fields:", formFields);

    const findUpdatedFields = formFields.filter((field) =>
      draggedIds.has(field.id)
    );
    console.log("Find updated fields:", findUpdatedFields);

    try {
      const response = await updateMarkFormField(formid, findUpdatedFields);
      console.log("Response:", response);
      setReOrderFlag(false);
      alert("Form saved successfully!");
    } catch (error) {
      console.error("Error saving form:", error);
      alert("Error saving form. Please try again.");
    }
  };

  return (
    <div className="container mx-auto p-6">
      {showDeleteModal && (
        <DeleteModal
          text={"Are you sure you want to delete this field?"}
          funct={(confirm) => handleDeleteFormField(confirm, deleteFieldId)}
        />
      )}

      <div className="flex flex-col sm:flex-row mb-10 items-center relative">
        <div className="absolute left-0 top-0 sm:relative sm:mr-4">
          <BackButton />
        </div>

        <h1 className="text-5xl text-gray-300 sm:text-5xl w-full text-center font-extrabold mb-4 sm:mb-0">
          Modify form
        </h1>
      </div>
      <div className="bg-gray-50 p-10 mb-6 rounded-lg border">
        <h2 className="text-xl font-semibold mb-4">Add New Field</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block mb-2">Field Type</label>
            <select
              onChange={handleSelectField}
              className="w-full border border-gray-300 p-2 rounded"
              value={selectedField}
            >
              <option value="input">Text Input</option>
              <option value="textarea">Text Area</option>
              <option value="checkbox">Checkbox</option>
              <option value="select">Select Field</option>
              <option value="disclaimer">Disclaimer</option>
            </select>
          </div>

          <div>
            <label htmlFor="label" className="block mb-2">
              Field Label
            </label>
            <input
              className="w-full border border-gray-300 p-2 rounded"
              placeholder={
                selectedField === "disclaimer" 
                  ? "Enter disclaimer text here..." 
                  : selectedField === "input" 
                    ? "e.g. Full Name" 
                    : selectedField === "textarea" 
                      ? "e.g. Message" 
                      : selectedField === "checkbox" 
                        ? "e.g. I agree to the terms and conditions"
                        : "Field Label"
              }
              type="text"
              id="label"
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
                setShowFieldLabelEmptyError(false);
              }}
            />
            {showFieldLabelEmptyError && (<>
              <p className="text-red-500 text-sm">
                    {errorMessage}
                  </p>
            </>
              
            )}

            {selectedField === "select" && (
              <div>
                <p>Add the name of the options:</p>

                {inputFields.map((val, index) => (
                  <input
                    key={index}
                    className="w-full border border-gray-300 p-2 rounded mb-2"
                    placeholder={`Option ${index + 1}`}
                    value={val}
                    type="text"
                    onChange={(event) => {
                      //The empty string gets the value of the newly added input field
                      const newInputFields = [...inputFields];

                      //Get the value
                      newInputFields[index] = event.target.value;

                      setInputFields(newInputFields);
                      setShowSelectFieldError(false);
                    }}
                  />
                ))}
                {showSelectFieldError && (
                  <p className="text-red-500 text-sm">
                    {errorMessage}
                  </p>
                )}
                    
               
                    <div className="flex items-center justify-between">

                <button
                  disabled={inputFields[0] === ""}
                  type="button"
                  className={`bg-blue-500 text-white px-3 py-1 rounded mt-2 ${
                    inputFields[0] === "" ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => setInputFields([...inputFields, ""])}
                >
                  Add another field
                </button>

                {/* Delete input field button */}
                {inputFields.length > 1 && (
                  <button
                    type="button"
                    className="bg-red-500 text-white px-3 py-1 rounded mt-2 ml-2"
                    onClick={() => {
                      const newInputFields = [...inputFields];
                      newInputFields.pop();
                      setInputFields(newInputFields);
                    }}
                  >
                    Remove last field
                  </button>
                )}
              </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="required"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
            className="mr-2"
          />
          <label htmlFor="required">Required Field</label>
        </div>
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="show_on_slack"
            checked={showOnSlack}
            onChange={(event) => setShowOnSlack(event.target.checked)}
            className="mr-2"
          />
          <label htmlFor="show_on_slack">Show on slack</label>
        </div>

        <button
          onClick={handleSubmit}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit(e);
            }
          }}
        >
          Add Field
        </button>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">
          Form Fields ({formFields.length})
        </h2>
        {formFields.length === 0 ? (
          <p className="text-gray-500">
            No fields added yet. Add fields above.
          </p>
        ) : (
          <DndContext
            onDragEnd={handleDragEnd}
            collisionDetection={closestCenter}
          >
            <p className="text-sm text-gray-500 mb-2">Drag to reorder fields</p>
            <SortableContext
              items={formFields.map((field) => field.id)}
              strategy={verticalListSortingStrategy}
            >
              {formFields
                .sort((a, b) => a.order - b.order)
                .map((field) => (
                  <DraggableField
                    key={field.id}
                    id={field.id}
                    field={field}
                    handleDeleteModal={handleDeleteModal}
                  />
                ))}

                
                   
              
            </SortableContext>
          </DndContext>
        )}
      </div>

      <button
        onClick={handleUpdateFieldOrder}
        className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600"
      >
        Save Form
      </button>
    </div>
  );
}

export default CreateDynamicForm;
